import { yupResolver } from '@hookform/resolvers/yup';
import { Search } from 'lucide-react';
import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/react';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import * as yup from 'yup';

import { Button } from '@/components/button';
import { CarrierLogo } from '@/components/carrier-logo';
import { Heading } from '@/components/heading';
import { TextInput } from '@/components/inputs';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import {
  MODE_AIR,
  SHIPMENT_TRACKING_TYPES,
  TRACKING_IMPORT_SINGLE_URL,
  USER_ACCESS_FEATURES,
  VEHICLE_TYPES,
} from '@/lib/constants';
import { marketingPages } from '@/lib/constants/marketing-site';
import { lockedDialogIsOpenState } from '@/state/user';
import { CarrierTrackingInfoType } from '@/types/api-types';
import { canAccessFeature } from '@/utils/auth';
import { createQueryString } from '@/utils/helpers';
import { trackUser } from '@/utils/tracking';

const validationSchema = yup.object({
  trackingNumber: yup.string().required('Please supply a tracking number'),
});

interface FormInputs {
  trackingNumber: string;
}

export interface ShipmentLiteTrackingFormProps {
  carrierId: string;
  carrierName: string;
  carrierTrackingId?: string;
  type?: CarrierTrackingInfoType;
}

const ShipmentLiteTrackingForm = ({
  carrierId,
  carrierTrackingId,
  carrierName,
  type = 'INTERMODAL',
}: ShipmentLiteTrackingFormProps) => {
  let userStatus = 'anonAccount';

  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setLockedDialogIsOpen = useSetRecoilState(lockedDialogIsOpenState);

  const { data: session } = useSession();
  const isLoggedIn = !!session;
  const canAccessTracking = canAccessFeature(USER_ACCESS_FEATURES.TRACKING, session);

  if (isLoggedIn) {
    userStatus = canAccessTracking ? 'paidAccount' : 'freeAccount';
  }

  const isAir = type.toLowerCase() === MODE_AIR;
  const titleString = isAir ? 'AWB Tracking' : 'Container Tracking';
  const trackingItem = isAir ? 'air cargo' : 'shipping containers';
  const vehicleItem = isAir ? VEHICLE_TYPES.AIRCRAFT : VEHICLE_TYPES.VESSEL;
  const trackingType = isAir ? 'AWB number' : 'Container ID';
  const inputPlaceHolder = isAir ? 'AWB (e.g. 12312345675)' : `Container ID (e.g. ${carrierId}1234567)`;
  const trackingNumberType = isAir ? SHIPMENT_TRACKING_TYPES.AWB_NUMBER : SHIPMENT_TRACKING_TYPES.CONTAINER_NUMBER;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
  });

  const handleTrackingSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { trackingNumber } = data;

    if (!trackingNumber) return;

    setIsLoading(true);

    trackUser.event('Submit Carrier Tracking Form', {
      carrierName,
      type,
      trackingNumber,
      trackingNumberType,
      userStatus,
    });

    trackUser.conversion({ category: 'CarrierTrackingForm', action: 'SubmittedTrackingNumber' });

    if (isLoggedIn) {
      if (canAccessTracking) {
        // User is logged in and can access tracking, redirect to new shipment form
        const params = createQueryString({
          trackingNumber: data.trackingNumber,
          trackingNumberType,
          mode: type.toLowerCase(),
          ...(!isAir ? { carrier: carrierTrackingId || carrierId } : {}),
        });
        const url = `${TRACKING_IMPORT_SINGLE_URL}${params}`;
        router.push(url);
      } else {
        // User is logged in, but doesn't have tracking access, show locked popup
        setLockedDialogIsOpen(true);
        setIsLoading(false);
      }
    } else {
      // If user is not logged in, redirect to login page
      signIn();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleTrackingSubmit)} className="w-full max-w-md">
      <fieldset className="rounded-lg bg-white p-6 shadow">
        <div className="mb-3 flex items-center gap-2">
          {carrierId && <CarrierLogo carrierCode={carrierId} mode="water" />}
          <Heading variant="h4" className="m-0 font-semibold">
            {carrierName} {titleString}
          </Heading>
        </div>
        <Text className="text-baseSm leading-snug">
          Get tracking milestones and estimated time of arrival for {trackingItem} on any {carrierName} {vehicleItem}{' '}
          using the {trackingType} supplied by the carrier or freight forwarder.
        </Text>
        <div className="flex items-start gap-4">
          <Controller
            control={control}
            name="trackingNumber"
            defaultValue=""
            render={({ field }) => {
              return (
                <TextInput
                  {...field}
                  placeholder={inputPlaceHolder}
                  className="flex-1 [&_p]:absolute [&_p]:-bottom-3 [&_p]:left-0 [&_p]:text-sm"
                  errors={errors}
                />
              );
            }}
          />
          <Button disabled={isLoading} isLoading={isLoading} iconLeft={<Search className="size-4" />}>
            Track
          </Button>
        </div>
      </fieldset>
      <Text variant="sm" className="mx-6 mb-0 mt-2 bg-lightBlue-50/60 italic text-grey-600">
        Note: An active Fluent Cargo subscription is needed to track {trackingItem}. See our full list of{' '}
        <Link className="underline" href={marketingPages.plans.url}>
          plans and inclusions.
        </Link>
      </Text>
    </form>
  );
};

export { ShipmentLiteTrackingForm };
